import { ChangeDetectorRef, Component, inject, input, model, OnInit, output } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { AccordionModule } from 'primeng/accordion';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { PaginatorModule } from 'primeng/paginator';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';

import { PessoaDomain } from '@/core/api/sicom-api/registro/pessoa/pessoa.domain';
import { AuthService } from '@/core/auth/auth.service';
import { SelectEstado } from '@/shared/components/layout/select-estado/select-estado';
import type { PessoaCreateRequestModel, PessoaRequestModel, PessoaResponseModel, ReferenciaOpcaoModel } from '@/shared/components/pessoa/pesquisa/pessoa.model';
import { PesquisaService } from '@/shared/components/pessoa/pesquisa/pesquisa.service';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
interface TipoPessoaOpcao {
    name: string;
    code: string;
}

type TipoPessoaApi = 'MEDICO' | 'PRESTADOR' | 'PREMEDICO' | 'PREPRESTADOR' | 'OUTROSPF' | 'OUTROSPJ' | 'ORGAO';
type TipoPessoaCadastro = 'OUTROSPF' | 'OUTROSPJ';

@Component({
    selector: 'app-pesquisar-pessoa-custom',
    standalone: true,
    imports: [DialogoPadraoDirective, ReactiveFormsModule, FormsModule, SelectModule, InputTextModule, ButtonModule, TableModule, DialogModule, PaginatorModule, SelectEstado, AutoCompleteModule, AccordionModule],
    templateUrl: './pesquisar-pessoa-custom.html',
    styleUrl: './pesquisar-pessoa-custom.scss'
})
export class PesquisarPessoaCustomComponent implements OnInit {
    private readonly pesquisaService = inject(PesquisaService);
    private readonly messageService = inject(MessageService);
    private readonly fb = inject(FormBuilder);
    private readonly cdRef = inject(ChangeDetectorRef);
    private readonly authService = inject(AuthService);

    pessoa = output<PessoaDomain>();
    showDialog = model<boolean>(false);
    tiposPessoaPermitidos = input<TipoPessoaApi[] | null>(null);

    selectedPessoa?: PessoaResponseModel;
    pessoas: PessoaResponseModel[] = [];
    carregando = false;
    cadastrandoPessoa = false;
    erro: string | null = null;
    scrollHeight = '400px';
    totalRecords = 0;
    rows = 10;
    first = 0;
    showDialogNovaPessoa = false;
    mostrarFiltrosAvancados = false;

    // Listas de referência (carregadas via API) para os dropdowns de prestador.
    situacoes: ReferenciaOpcaoModel[] = [];
    tiposInscricao: ReferenciaOpcaoModel[] = [];
    classificacoes: ReferenciaOpcaoModel[] = [];
    municipios: ReferenciaOpcaoModel[] = [];
    subtipos: ReferenciaOpcaoModel[] = [];
    carregandoSubtipos = false;
    private referenciasCarregadas = false;

    // Estado do autocomplete de CNAE: objeto selecionado + sugestões correntes.
    cnaeSelecionado: ReferenciaOpcaoModel | null = null;
    cnaesSugeridos: ReferenciaOpcaoModel[] = [];

    // Listas fixas (constantes) para os dropdowns de prestador (rótulos conforme legado).
    readonly categorias: ReferenciaOpcaoModel[] = [
        { value: 'C', label: 'Civil' },
        { value: 'L', label: 'Civil Ltda.' },
        { value: 'S', label: 'Civil S/A' },
        { value: 'M', label: 'Militar' },
        { value: 'E', label: 'Eireli' }
    ];
    readonly regCads: ReferenciaOpcaoModel[] = [
        { value: 'R', label: 'Registro' },
        { value: 'C', label: 'Cadastro' }
    ];
    readonly instituidos: ReferenciaOpcaoModel[] = [
        { value: 'P', label: 'Poder Público' },
        { value: 'F', label: 'Fundação' },
        { value: 'A', label: 'Autarquia' },
        { value: 'S', label: 'Sindicato' }
    ];
    readonly gestaoTipos: ReferenciaOpcaoModel[] = [
        { value: 'P', label: 'Pública' },
        { value: 'R', label: 'Privada' },
        { value: 'O', label: 'Terceirizada' }
    ];
    readonly filtroClassificacoes: ReferenciaOpcaoModel[] = [
        { value: '0', label: 'Todas' },
        { value: '1', label: 'Novas Classificações' },
        { value: '2', label: 'Transição' },
        { value: '3', label: 'Sem Classificação' }
    ];

    private readonly todosTiposPessoa: TipoPessoaOpcao[] = [
        { name: 'Medico', code: 'MEDICO' },
        { name: 'Prestador', code: 'PRESTADOR' },
        { name: 'Orgao', code: 'ORGAO' },
        { name: 'Outros PF', code: 'OUTROSPF' },
        { name: 'Outros PJ', code: 'OUTROSPJ' }
    ];

    readonly tiposCadastro: TipoPessoaOpcao[] = [
        { name: 'Outros PF', code: 'OUTROSPF' },
        { name: 'Outros PJ', code: 'OUTROSPJ' }
    ];

    uf = 'BR';
    ufsPermitidas: string[] | null = null;

    readonly formPesquisa = this.fb.nonNullable.group({
        tipo: [''],
        uf: ['BR'],
        nome: ['', [Validators.required, Validators.minLength(3)]],
        // Filtros avançados de prestador (PJ) - usados apenas quando o tipo selecionado é PRESTADOR.
        cnpj: [''],
        nomeFantasia: [''],
        nomeUsual: [''],
        situacao: [''],
        endereco: [''],
        municipioId: [''],
        categoria: [''],
        regCad: [''],
        instituido: [''],
        tipoInscricao: [''],
        classificacao: [''],
        cnae: [''],
        anotacoes: [''],
        cep: [''],
        gestaoTipo: [''],
        filtroClassificacao: [''],
        subtipoClassificacao: ['']
    });

    readonly formNovaPessoa = this.fb.nonNullable.group({
        tipo: ['OUTROSPF', [Validators.required]],
        uf: ['BR', [Validators.required, Validators.minLength(2), Validators.maxLength(2)]],
        nome: ['', [Validators.required, Validators.minLength(3)]],
        cpfOuCnpj: ['', [Validators.required, Validators.minLength(11)]],
        nomeSocial: [''],
        email: [''],
        homepage: ['']
    });

    ngOnInit(): void {
        this.configurarUfPorToken();
        this.formPesquisa.patchValue({ uf: this.uf });
        this.formNovaPessoa.patchValue({ uf: this.uf });
        // Porte/Tipo só é habilitado quando há uma Classificação selecionada.
        this.formPesquisa.controls.subtipoClassificacao.disable();
    }

    onShow(): void {
        this.scrollHeight = '400px';
        this.limparFormulario();
    }

    onDialogMaximize(event: unknown): void {
        const maximized = typeof event === 'object' && event !== null && 'maximized' in event ? !!(event as { maximized?: boolean }).maximized : false;
        setTimeout(() => {
            this.scrollHeight = maximized ? 'calc(100vh - 250px)' : '400px';
            this.cdRef.detectChanges();
        }, 0);
    }

    onPageChange(event: { first?: number; rows?: number }): void {
        this.first = event.first ?? 0;
        this.rows = event.rows ?? this.rows;
        this.pesquisar(event);
    }

    changeuf(uf: string | null | undefined): void {
        if (uf === null || uf === undefined) {
            return;
        }
        this.formPesquisa.patchValue({ uf });
        // Ao trocar a UF, recarrega a lista de municípios e limpa o município selecionado.
        this.formPesquisa.controls.municipioId.setValue('');
        this.carregarMunicipios(uf);
    }

    changeUfNovaPessoa(uf: string | null | undefined): void {
        if (uf === null || uf === undefined) {
            return;
        }
        this.formNovaPessoa.patchValue({ uf });
    }

    abrirFormularioNovaPessoa(): void {
        this.showDialogNovaPessoa = true;
        this.formNovaPessoa.reset({
            tipo: 'OUTROSPF',
            uf: this.formPesquisa.controls.uf.value || this.uf,
            nome: '',
            cpfOuCnpj: '',
            nomeSocial: '',
            email: '',
            homepage: ''
        });
    }

    cancelarCadastroNovaPessoa(): void {
        this.showDialogNovaPessoa = false;
    }

    cadastrarNovaPessoa(): void {
        if (this.formNovaPessoa.invalid) {
            this.formNovaPessoa.markAllAsTouched();
            this.messageService.add({
                severity: 'warn',
                summary: 'Dados invalidos',
                detail: 'Preencha os campos obrigatorios para cadastrar a pessoa.'
            });
            return;
        }

        const tipoCadastro = this.normalizeTipoCadastro(this.formNovaPessoa.controls.tipo.value);
        if (!tipoCadastro) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Tipo invalido',
                detail: 'Para cadastro, informe tipo OUTROSPF ou OUTROSPJ.'
            });
            return;
        }

        const payload: PessoaCreateRequestModel = {
            tipo: tipoCadastro,
            uf: this.formNovaPessoa.controls.uf.value.trim().toUpperCase(),
            nome: this.formNovaPessoa.controls.nome.value.trim(),
            cpfOuCnpj: this.formNovaPessoa.controls.cpfOuCnpj.value.trim(),
            nomeSocial: this.valorOpcional(this.formNovaPessoa.controls.nomeSocial.value),
            email: this.valorOpcional(this.formNovaPessoa.controls.email.value),
            homepage: this.valorOpcional(this.formNovaPessoa.controls.homepage.value)
        };

        this.cadastrandoPessoa = true;
        this.pesquisaService.cadastrarPessoa(payload).subscribe({
            next: () => {
                this.cadastrandoPessoa = false;
                this.showDialogNovaPessoa = false;
                this.messageService.add({
                    severity: 'success',
                    summary: 'Pessoa cadastrada',
                    detail: 'Cadastro realizado com sucesso.'
                });

                this.formPesquisa.patchValue({
                    nome: payload.nome,
                    tipo: payload.tipo,
                    uf: payload.uf
                });
                this.first = 0;
                this.pesquisar({ first: 0, rows: this.rows });
            },
            error: (err: Error) => {
                this.cadastrandoPessoa = false;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Erro no cadastro',
                    detail: err.message
                });
            }
        });
    }

    limparFormulario(): void {
        this.formPesquisa.reset({
            tipo: this.getTipoPessoaPadraoPesquisa(),
            uf: this.uf,
            nome: '',
            cnpj: '',
            nomeFantasia: '',
            nomeUsual: '',
            situacao: '',
            endereco: '',
            municipioId: '',
            categoria: '',
            regCad: '',
            instituido: '',
            tipoInscricao: '',
            classificacao: '',
            cnae: '',
            anotacoes: '',
            cep: '',
            gestaoTipo: '',
            filtroClassificacao: '',
            subtipoClassificacao: ''
        });
        this.cnaeSelecionado = null;
        this.cnaesSugeridos = [];
        this.subtipos = [];
        this.formPesquisa.controls.subtipoClassificacao.disable();
        this.mostrarFiltrosAvancados = false;
        this.formNovaPessoa.reset({
            tipo: 'OUTROSPF',
            uf: this.uf,
            nome: '',
            cpfOuCnpj: '',
            nomeSocial: '',
            email: '',
            homepage: ''
        });
        this.showDialogNovaPessoa = false;
        this.pessoas = [];
        this.selectedPessoa = undefined;
        this.totalRecords = 0;
        this.first = 0;
        this.erro = null;
    }

    pesquisar(event?: { first?: number; rows?: number }): void {
        const nomeParaBusca = this.formPesquisa.controls.nome.value.trim();
        this.garantirTipoPessoaPermitidoSelecionado();

        if (nomeParaBusca.length < 3) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Criterio invalido',
                detail: 'Informe ao menos 3 caracteres no campo Nome / Razao Social.'
            });
            return;
        }

        this.carregando = true;
        this.erro = null;
        this.selectedPessoa = undefined;

        const tipoSelecionado = this.normalizeTipoToApi(this.formPesquisa.controls.tipo.value);

        const filtros: PessoaRequestModel = {
            nome: nomeParaBusca,
            tipo: tipoSelecionado,
            uf: this.formPesquisa.controls.uf.value || null || undefined
        };

        if (tipoSelecionado === 'PRESTADOR') {
            // CNPJ alfanumérico (padrão vigente a partir de 2026): mantém letras e números,
            // removendo só a formatação (pontos, barra, hífen, espaços); normaliza em maiúsculas.
            const cnpj = this.formPesquisa.controls.cnpj.value.replace(/[^0-9A-Za-z]/g, '').toUpperCase();
            const nomeFantasia = this.formPesquisa.controls.nomeFantasia.value.trim();
            const nomeUsual = this.formPesquisa.controls.nomeUsual.value.trim();
            const situacao = this.formPesquisa.controls.situacao.value.trim();
            const endereco = this.formPesquisa.controls.endereco.value.trim();
            const municipioId = this.formPesquisa.controls.municipioId.value.trim();
            const categoria = this.formPesquisa.controls.categoria.value.trim();
            const regCad = this.formPesquisa.controls.regCad.value.trim();
            const instituido = this.formPesquisa.controls.instituido.value.trim();
            const tipoInscricao = this.formPesquisa.controls.tipoInscricao.value.trim();
            const classificacao = this.formPesquisa.controls.classificacao.value.trim();
            const cnae = this.formPesquisa.controls.cnae.value.trim();
            const anotacoes = this.formPesquisa.controls.anotacoes.value.trim();
            const cep = this.formPesquisa.controls.cep.value.replace(/\D/g, '');
            const gestaoTipo = this.formPesquisa.controls.gestaoTipo.value.trim();
            const filtroClassificacao = this.formPesquisa.controls.filtroClassificacao.value.trim();
            const subtipoClassificacao = this.formPesquisa.controls.subtipoClassificacao.value.trim();

            if (cnpj) {
                filtros.cnpj = cnpj;
            }
            if (nomeFantasia) {
                filtros.nomeFantasia = nomeFantasia;
            }
            if (nomeUsual) {
                filtros.nomeUsual = nomeUsual;
            }
            if (situacao && !Number.isNaN(Number(situacao))) {
                filtros.situacao = Number(situacao);
            }
            if (endereco) {
                filtros.endereco = endereco;
            }
            if (municipioId && !Number.isNaN(Number(municipioId))) {
                filtros.municipioId = Number(municipioId);
            }
            if (categoria) {
                filtros.categoria = categoria;
            }
            if (regCad) {
                filtros.regCad = regCad;
            }
            if (instituido) {
                filtros.instituido = instituido;
            }
            if (tipoInscricao && !Number.isNaN(Number(tipoInscricao))) {
                filtros.tipoInscricao = Number(tipoInscricao);
            }
            if (classificacao && !Number.isNaN(Number(classificacao))) {
                filtros.classificacao = Number(classificacao);
            }
            if (cnae) {
                filtros.cnae = cnae;
            }
            if (anotacoes) {
                filtros.anotacoes = anotacoes;
            }
            if (cep) {
                filtros.cep = cep;
            }
            if (gestaoTipo) {
                filtros.gestaoTipo = gestaoTipo;
            }
            if (filtroClassificacao && !Number.isNaN(Number(filtroClassificacao))) {
                filtros.filtroClassificacao = Number(filtroClassificacao);
            }
            if (subtipoClassificacao && !Number.isNaN(Number(subtipoClassificacao))) {
                filtros.subtipoClassificacao = Number(subtipoClassificacao);
            }
        }

        const first = event?.first ?? 0;
        const rows = event?.rows ?? this.rows;
        const page = rows > 0 ? first / rows : 0;
        const size = rows;
        this.rows = size;

        this.pesquisaService.pesquisarPessoas(filtros, page, size).subscribe({
            next: (resposta) => {
                this.pessoas = resposta.content;
                this.totalRecords = resposta.totalElements ?? 0;
                this.carregando = false;
            },
            error: (err: Error) => {
                this.erro = err.message;
                this.carregando = false;
                this.messageService.add({ severity: 'error', summary: 'Erro na pesquisa', detail: err.message });
            }
        });
    }

    selecionar(): void {
        if (!this.selectedPessoa?.id) {
            return;
        }

        const payload: PessoaResponseModel = {
            id: this.selectedPessoa.id,
            uf: this.selectedPessoa.uf ?? '',
            nome: this.selectedPessoa.nome ?? '',
            nomeSocial: this.selectedPessoa.nomeSocial ?? '',
            crm: this.selectedPessoa.crm ?? '',
            registro: this.selectedPessoa.registro ?? '',
            tipo: this.normalizeTipoToApi(this.selectedPessoa.tipo) ?? ''
        };

        this.pessoa.emit(payload as PessoaDomain);
        this.hideDialog();
    }

    hideDialog(): void {
        this.showDialog.set(false);
    }

    getTiposPessoaPesquisa(): TipoPessoaOpcao[] {
        const permitidos = this.tiposPessoaPermitidos();
        if (!permitidos?.length) {
            return this.todosTiposPessoa;
        }

        return this.todosTiposPessoa.filter((tipo) => permitidos.includes(tipo.code as TipoPessoaApi));
    }

    hasTiposPessoaRestritos(): boolean {
        return !!this.tiposPessoaPermitidos()?.length;
    }

    /** Indica se o tipo atualmente selecionado é PRESTADOR (habilita os filtros avançados de PJ). */
    isPrestadorSelecionado(): boolean {
        return this.normalizeTipoToApi(this.formPesquisa.controls.tipo.value) === 'PRESTADOR';
    }

    toggleFiltrosAvancados(): void {
        this.mostrarFiltrosAvancados = !this.mostrarFiltrosAvancados;
        if (this.mostrarFiltrosAvancados) {
            this.carregarReferenciasPrestador();
        }
    }

    /** Carrega (uma única vez) as listas de referência dos dropdowns de prestador. */
    carregarReferenciasPrestador(): void {
        if (this.referenciasCarregadas) {
            return;
        }
        this.referenciasCarregadas = true;

        this.pesquisaService.listarSituacoes().subscribe({
            next: (opcoes) => (this.situacoes = opcoes),
            error: () => (this.referenciasCarregadas = false)
        });
        this.pesquisaService.listarTiposInscricao().subscribe({
            next: (opcoes) => (this.tiposInscricao = opcoes)
        });
        this.pesquisaService.listarClassificacoes().subscribe({
            next: (opcoes) => (this.classificacoes = opcoes)
        });
        this.carregarMunicipios(this.formPesquisa.controls.uf.value);
    }

    /** Carrega a lista completa de municípios da UF informada (para o p-select). */
    carregarMunicipios(uf: string | null | undefined): void {
        this.pesquisaService.listarMunicipiosPorUf(uf).subscribe({
            next: (opcoes) => (this.municipios = opcoes)
        });
    }

    /** Recarrega os subtipos (Porte/Tipo) ao mudar a Classificação; limpa a seleção anterior. */
    onClassificacaoChange(): void {
        const classificacaoId = this.formPesquisa.controls.classificacao.value;
        const subtipoControl = this.formPesquisa.controls.subtipoClassificacao;
        subtipoControl.setValue('');
        this.subtipos = [];
        if (!classificacaoId) {
            subtipoControl.disable();
            return;
        }
        subtipoControl.enable();
        this.carregandoSubtipos = true;
        this.pesquisaService.listarSubtiposClassificacao(classificacaoId).subscribe({
            next: (opcoes) => {
                this.subtipos = opcoes;
                this.carregandoSubtipos = false;
            },
            error: () => (this.carregandoSubtipos = false)
        });
    }

    /** completeMethod do autocomplete de CNAE. */
    buscarCnaes(event: { query: string }): void {
        this.pesquisaService.buscarCnaes(event.query).subscribe({
            next: (opcoes) => (this.cnaesSugeridos = opcoes)
        });
    }

    /** Guarda o código do CNAE selecionado no control; limpa quando o campo é esvaziado. */
    onSelectCnae(opcao: ReferenciaOpcaoModel | null): void {
        this.formPesquisa.controls.cnae.setValue(opcao?.value ?? '');
    }

    private configurarUfPorToken(): void {
        const ufToken = this.authService.ufDoUsuario()?.trim().toUpperCase() ?? '';
        const ufTokenValida = /^[A-Z]{2}$/.test(ufToken) ? ufToken : '';

        if (ufTokenValida && ufTokenValida !== 'BR') {
            this.uf = ufTokenValida;
            this.ufsPermitidas = [ufTokenValida];
            return;
        }

        this.uf = 'BR';
        this.ufsPermitidas = null;
    }

    private normalizeTipoToApi(tipo?: string | null): TipoPessoaApi | undefined {
        if (!tipo) {
            return undefined;
        }

        const normalized = tipo.trim().toUpperCase();
        if (normalized === 'OUTROSPF' || normalized === 'OUTROSPJ') {
            return normalized;
        }
        if (normalized === 'MEDICO' || normalized === 'PRESTADOR' || normalized === 'PREMEDICO' || normalized === 'PREPRESTADOR' || normalized === 'ORGAO') {
            return normalized;
        }

        return undefined;
    }

    private normalizeTipoCadastro(tipo?: string | null): TipoPessoaCadastro | undefined {
        const normalized = this.normalizeTipoToApi(tipo);
        if (normalized === 'OUTROSPF' || normalized === 'OUTROSPJ') {
            return normalized;
        }
        return undefined;
    }

    private getTipoPessoaPadraoPesquisa(): string {
        return this.hasTiposPessoaRestritos() ? (this.getTiposPessoaPesquisa()[0]?.code ?? '') : '';
    }

    private garantirTipoPessoaPermitidoSelecionado(): void {
        if (!this.hasTiposPessoaRestritos()) {
            return;
        }

        const tipoAtual = this.normalizeTipoToApi(this.formPesquisa.controls.tipo.value);
        const tipoPermitido = this.getTiposPessoaPesquisa().some((tipo) => tipo.code === tipoAtual);
        if (!tipoAtual || !tipoPermitido) {
            this.formPesquisa.controls.tipo.setValue(this.getTipoPessoaPadraoPesquisa());
        }
    }

    private valorOpcional(valor?: string | null): string | undefined {
        if (!valor) {
            return undefined;
        }
        const trimmed = valor.trim();
        return trimmed.length > 0 ? trimmed : undefined;
    }
}
